import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, DestroyRef, OnInit, inject, input, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { MatIconModule } from '@angular/material/icon';
import { MatPaginatorModule, PageEvent } from '@angular/material/paginator';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { EMPTY, catchError, finalize } from 'rxjs';
import { ScanStatusBadgesComponent } from '@/tms/gbox-scans/_components/scan-status-badges/scan-status-badges.component';
import { Scan } from '@/tms/gbox-scans/_models/gbox-scans.model';
import { ReportsApiService } from '@/tms/reports/_api/reports.api.service';
import { ReportAnswersTableComponent } from '@/tms/reports/_components/report-answers-table/report-answers-table.component';
import { GboxResponseRow, PAYMENT_METHOD_ANSWER_FILTER } from '@/tms/reports/_models/reports.model';
import { FirstScanRefPipe } from '@/tms/reports/_pipes/first-scan-ref.pipe';
import { applyScanStatusUpdate } from '@/tms/reports/_utils/apply-scan-status-update.util';

@Component({
	selector: 'app-driver-scanned-documents',
	standalone: true,
	changeDetection: ChangeDetectionStrategy.OnPush,
	imports: [
		CommonModule,
		MatIconModule,
		MatPaginatorModule,
		MatProgressSpinnerModule,
		ReportAnswersTableComponent,
		ScanStatusBadgesComponent,
		FirstScanRefPipe,
	],
	templateUrl: './driver-scanned-documents.component.html',
	styleUrl: './driver-scanned-documents.component.scss',
})
export class DriverScannedDocumentsComponent implements OnInit {
	readonly driverId = input.required<string>();

	readonly rows = signal<GboxResponseRow[]>([]);
	readonly total = signal(0);
	readonly pageIndex = signal(0);
	readonly pageSize = signal(25);
	readonly pageSizeOptions = [10, 25, 50, 100];
	readonly isLoading = signal(false);
	readonly expandedId = signal<number | null>(null);

	private readonly api = inject(ReportsApiService);
	private readonly destroyRef = inject(DestroyRef);

	ngOnInit(): void {
		this.loadPage();
	}

	onPage(event: PageEvent): void {
		this.pageIndex.set(event.pageIndex);
		this.pageSize.set(event.pageSize);
		this.loadPage();
	}

	toggleExpand(row: GboxResponseRow): void {
		this.expandedId.update((current) => (current === row.id ? null : row.id));
	}

	applyScanUpdate(scan: Scan): void {
		this.rows.update((rows) => applyScanStatusUpdate(rows, scan));
	}

	private loadPage(): void {
		this.isLoading.set(true);
		this.api
			.list(this.pageIndex() + 1, this.pageSize(), {
				from: null,
				to: null,
				reportId: null,
				boxId: null,
				driverId: null,
				driverUuid: this.driverId(),
				answerFilter: PAYMENT_METHOD_ANSWER_FILTER,
			})
			.pipe(
				catchError(() => EMPTY),
				finalize(() => this.isLoading.set(false)),
				takeUntilDestroyed(this.destroyRef),
			)
			.subscribe((res) => {
				this.expandedId.set(null);
				this.rows.set(res.data ?? []);
				this.total.set(res.meta?.total ?? 0);
			});
	}
}
